/**
 * The language selector, as items for the profile menu (V1.md section 18).
 *
 * Not a control of its own. Changing language is rare and deliberate, and the
 * menu that already holds the account and sign out is where it belongs — a
 * fourth thing in the header would compete with the page title and the search
 * box for a choice most people make once.
 *
 * Choosing one does two things, in this order:
 *
 * 1. Navigates to the same page under the new prefix. First, because it is the
 *    part the reader is waiting for: the URL is what decides the language of
 *    the page, so the screen is translated the moment the route changes and
 *    does not wait on a round trip.
 * 2. Stores the preference, so the *next* language-less URL redirects here
 *    (section 37).
 *
 * The store is fire-and-forget on purpose. A failed write leaves the person
 * reading the language they asked for, which is right, and the worst case is
 * that a later `/learn` redirects to the old default — recoverable by choosing
 * again, and not worth an error banner over a page that already changed.
 */
import { useMutation } from '@apollo/client/react'
import { toLanguageCode } from '@athena/i18n'
import { useLanguage, useTranslate, type ProfileMenuItem } from '@athena/ui'
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router'

import { MeDocument, UpdatePreferredLanguageDocument } from '../graphql/generated/operations.js'
import { LANGUAGES, withLanguage, type Language } from '../lib/language.js'

/** Where `navigate` will accept a `to`. Derived, so a route rename reaches it. */
type Destination = Parameters<ReturnType<typeof useNavigate>>[0] extends { to?: infer T }
  ? T
  : never

export function useLanguageMenuItems(): ProfileMenuItem[] {
  const t = useTranslate()
  const current = useLanguage()
  const navigate = useNavigate()

  const [store] = useMutation(UpdatePreferredLanguageDocument, {
    // The shell reads `me` for the name and the email, and the router reads
    // `preferredLanguage` off the same query to decide redirects. Refetching
    // keeps the second one honest without a hand-written cache write.
    refetchQueries: [MeDocument],
  })

  /**
   * The route this page is, as a path template — `/$language/practices/$practiceId`
   * rather than `/en/practices/p1`.
   *
   * This plus the current params is what makes switching language
   * parameter-safe (section 19). Rewriting the URL text would work until a
   * practice was titled `es`; handing the router the same route with one
   * parameter changed cannot go wrong that way.
   */
  const route = useRouterState({ select: (state) => state.matches.at(-1)?.fullPath })
  /**
   * `fullPath` reports the tutor dashboard as `/$language/`, with the trailing
   * slash an index route carries, while `navigate` names it `/$language`. They
   * are the same route; the cast is only because the union of route paths does
   * not contain both spellings.
   */
  const target = (route === '/$language/' ? '/$language' : route) as Destination
  const params = useParams({ strict: false })
  const search = useRouterState({ select: (state) => state.location.search })
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  function choose(language: Language) {
    if (language === current) return

    if (target) {
      void navigate({
        to: target,
        params: { ...params, language },
        search,
        replace: true,
      })
    } else {
      // No match to re-enter — a not-found screen, say. The path is all there
      // is, and `withLanguage` rewrites only its first segment.
      void navigate({ href: withLanguage(pathname, language), replace: true })
    }

    void store({ variables: { language: toLanguageCode(language) } })
  }

  return LANGUAGES.map((language) => ({
    id: `language-${language}`,
    group: 'language',
    label: t(`language.${language}`),
    selected: language === current,
    onSelect: () => choose(language),
  }))
}
